/* eslint-disable react-refresh/only-export-components */
// File-level disable: this module intentionally exports both helpers
// (formatters + color classes) and React components. Fast Refresh
// rules don't apply here because the file is consumed by route
// modules that themselves are HMR-eligible. Splitting helpers into a
// separate non-tsx file is pure overhead.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type {
	FantasyLeaderboardEntry,
	LeaderboardBankrollTier,
} from "../../../lib/types";

// Shared visual primitives for the Fantasy Fams leaderboard routes. Kept in a
// single _shared.tsx so the weekly / quarterly / earnings routes don't fork
// the same UI three times.

// --- Formatters -------------------------------------------------------------

export function formatPctSigned(n: number | null | undefined): string {
	if (n === null || n === undefined || !Number.isFinite(n)) return "—";
	const sign = n > 0 ? "+" : "";
	return `${sign}${n.toFixed(2)}%`;
}

export function pctColorClass(n: number | null | undefined): string {
	if (n === null || n === undefined || !Number.isFinite(n)) {
		return "text-[var(--color-muted)]";
	}
	return n >= 0 ? "text-[var(--color-accent)]" : "text-[var(--color-danger)]";
}

// --- Bankroll pool tabs -----------------------------------------------------

const BANKROLL_TAB_LABELS: Record<LeaderboardBankrollTier, string> = {
	"10k": "$10K",
	"100k": "$100K",
	"1m": "$1M",
};

const BANKROLL_TAB_ORDER: readonly LeaderboardBankrollTier[] = ["10k", "100k", "1m"];

// BankrollTabs — the $10K / $100K / $1M competitive-pool selector shared by
// all three leaderboard routes. Each pool ranks independently (migration
// 043); custom-bankroll Fantasy Fams are unranked and have no tab.
//
// All three tabs are always visible (the paid pools stay advertised), but
// $100K / $1M are locked — disabled, not clickable — for free-tier users.
// Only $10K is selectable without a paid subscription.
export function BankrollTabs({
	value,
	onChange,
	isPaid,
}: {
	value: LeaderboardBankrollTier;
	onChange: (tier: LeaderboardBankrollTier) => void;
	isPaid: boolean;
}) {
	return (
		<div
			role="tablist"
			aria-label="Bankroll pool"
			className="flex gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-1"
		>
			{BANKROLL_TAB_ORDER.map((tier) => {
				const active = value === tier;
				const locked = tier !== "10k" && !isPaid;
				return (
					<button
						key={tier}
						type="button"
						role="tab"
						aria-selected={active}
						disabled={locked}
						title={
							locked
								? "Upgrade to a paid plan to view this bankroll pool"
								: undefined
						}
						onClick={() => {
							if (!locked) onChange(tier);
						}}
						className={`flex-1 rounded-md px-3 py-1.5 text-sm font-semibold transition ${
							active
								? "bg-[var(--color-accent)] text-black"
								: locked
									? "cursor-not-allowed text-[var(--color-muted)] opacity-50"
									: "text-[var(--color-muted)] hover:text-[var(--color-fg)]"
						}`}
					>
						{BANKROLL_TAB_LABELS[tier]}
					</button>
				);
			})}
		</div>
	);
}

// --- Rank chip --------------------------------------------------------------

// Top-3 get medal styling (gold/silver/bronze tints), everyone else gets the
// muted ring. The chip is fixed-width so multiple rows align vertically.
export function RankChip({ rank }: { rank: number }) {
	const styles =
		rank === 1
			? "border-yellow-400/60 bg-yellow-400/10 text-yellow-300"
			: rank === 2
				? "border-zinc-300/40 bg-zinc-300/10 text-zinc-100"
				: rank === 3
					? "border-amber-700/60 bg-amber-700/10 text-amber-300"
					: "border-[var(--color-border)] bg-[var(--color-bg-elev)] text-[var(--color-muted)]";
	return (
		<span
			className={`inline-flex h-9 w-9 items-center justify-center rounded-md border font-mono text-sm font-semibold ${styles}`}
		>
			{rank}
		</span>
	);
}

// --- Leaderboard row --------------------------------------------------------

interface LeaderboardRowProps {
	entry: FantasyLeaderboardEntry;
	isYours?: boolean;
	// When true, render the SPY-delta as the dominant figure with the raw
	// return as a secondary line. Used by the quarterly leaderboard.
	emphasizeSpyDelta?: boolean;
	// When true, render the ticker chip strip beneath the row.
	showTickers?: boolean;
}

export function LeaderboardRow({
	entry,
	isYours,
	emphasizeSpyDelta,
	showTickers,
}: LeaderboardRowProps) {
	// Decimal columns come over the wire as quoted strings (PG NUMERIC →
	// JSON). Parse here so the formatters can operate on numbers, and so a
	// missing/null value lands as NaN → formatter shows the "—" placeholder.
	const returnPctNum = parseFloat(entry.returnPct);
	const spyDeltaNum =
		entry.spyDelta !== undefined && entry.spyDelta !== null
			? parseFloat(entry.spyDelta)
			: undefined;
	const hero =
		emphasizeSpyDelta && spyDeltaNum !== undefined ? spyDeltaNum : returnPctNum;
	const heroLabel =
		emphasizeSpyDelta && spyDeltaNum !== undefined ? "vs SPY" : "return";

	const wrapperCls = [
		"block rounded-lg border bg-[var(--color-bg-card)] p-4 transition hover:border-[var(--color-accent)]",
		isYours
			? "border-[var(--color-accent)] ring-1 ring-[var(--color-accent)]/40"
			: "border-[var(--color-border)]",
	].join(" ");

	// famSlug is nullable in the worker response — render a static card
	// instead of a Link so the page doesn't navigate to `/fantasy-fam/null`.
	// Leaderboard entries are Fantasy Fam entities, so the link targets the
	// /fantasy-fam/:slug detail route.
	const Wrapper = ({ children }: { children: React.ReactNode }) =>
		entry.famSlug ? (
			<Link to={`/fantasy-fam/${entry.famSlug}`} className={wrapperCls}>
				{children}
			</Link>
		) : (
			<div className={wrapperCls}>{children}</div>
		);

	return (
		<Wrapper>
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
				<RankChip rank={entry.rank} />
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<p className="truncate text-base font-semibold">
							{entry.famName}
						</p>
						{isYours && (
							<span className="rounded-md border border-[var(--color-accent)]/40 bg-[var(--color-accent-soft)] px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-[var(--color-accent)]">
								You
							</span>
						)}
					</div>
					{entry.famSlug && (
						<p className="truncate font-mono text-xs text-[var(--color-muted)]">
							{entry.famSlug}
						</p>
					)}
				</div>
				<div className="flex flex-col items-start gap-0.5 text-left sm:items-end sm:text-right">
					<p
						className={`font-mono text-2xl font-bold tabular-nums ${pctColorClass(hero)}`}
					>
						{formatPctSigned(hero)}
					</p>
					<p className="text-[10px] uppercase tracking-wider text-[var(--color-muted)]">
						{heroLabel}
					</p>
					{emphasizeSpyDelta && spyDeltaNum !== undefined && (
						<p className="text-xs text-[var(--color-muted)]">
							raw{" "}
							<span className={`font-mono ${pctColorClass(returnPctNum)}`}>
								{formatPctSigned(returnPctNum)}
							</span>
						</p>
					)}
				</div>
			</div>
			{showTickers && entry.tickers && entry.tickers.length > 0 && (
				<div className="mt-3 flex flex-wrap gap-1.5">
					{entry.tickers.slice(0, 8).map((t) => (
						<span
							key={t}
							className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-muted)]"
						>
							{t}
						</span>
					))}
					{entry.tickers.length > 8 && (
						<span className="text-[10px] text-[var(--color-muted)]">
							+{entry.tickers.length - 8} more
						</span>
					)}
				</div>
			)}
		</Wrapper>
	);
}

// --- Settle countdown -------------------------------------------------------

// Lightweight countdown ticker. Re-renders once a second; bails to the
// "settled" copy once the target has passed. We don't bother with a portal /
// observability ticker since this is a single inline number.
export function SettleCountdown({
	target,
	label = "Settles in",
}: {
	target: string;
	label?: string;
}) {
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		const id = window.setInterval(() => setNow(Date.now()), 1000);
		return () => window.clearInterval(id);
	}, []);

	const targetMs = new Date(target).getTime();
	if (!Number.isFinite(targetMs)) return null;
	const deltaMs = targetMs - now;

	if (deltaMs <= 0) {
		return (
			<div className="text-right">
				<p className="text-[10px] uppercase tracking-wider text-[var(--color-muted)]">
					{label}
				</p>
				<p className="font-mono text-base font-semibold text-[var(--color-muted)]">
					Settled
				</p>
			</div>
		);
	}

	const totalSec = Math.floor(deltaMs / 1000);
	const days = Math.floor(totalSec / 86400);
	const hours = Math.floor((totalSec % 86400) / 3600);
	const minutes = Math.floor((totalSec % 3600) / 60);
	const seconds = totalSec % 60;

	const display =
		days > 0
			? `${days}d ${hours}h ${minutes}m`
			: hours > 0
				? `${hours}h ${minutes}m ${String(seconds).padStart(2, "0")}s`
				: `${minutes}m ${String(seconds).padStart(2, "0")}s`;

	return (
		<div className="text-right">
			<p className="text-[10px] uppercase tracking-wider text-[var(--color-muted)]">
				{label}
			</p>
			<p className="font-mono text-base font-semibold tabular-nums">{display}</p>
		</div>
	);
}
